/**
 * `SectionSwitcher` — selettore (dropdown) delle tre macro-sezioni.
 *
 * Mostra la sezione corrente (derivata dal path) e, all'apertura, l'elenco
 * delle sezioni a cui l'utente ha accesso. Selezionando una sezione naviga alla
 * sua `home` (prima voce). I link interni alla sezione restano nella `Navbar`.
 *
 * La meccanica di apertura/chiusura (click esterno, `Escape`, `aria-expanded`)
 * replica quella di `UserMenu`, per coerenza di comportamento e stile.
 *
 * Se l'utente ha accesso a una sola sezione il selettore non è interattivo:
 * mostra soltanto l'etichetta della sezione, senza dropdown (non ci sono
 * alternative fra cui scegliere).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  accessibleSections,
  sectionForPath,
  type SectionAccess,
} from './sections';
import { Icon } from './ui';

interface SectionSwitcherProps {
  /** Path corrente, per derivare la sezione attiva. */
  pathname: string;
  /** Contesto di accesso (ruolo + `levelsEnabled`) per filtrare le sezioni. */
  access: SectionAccess;
}

export function SectionSwitcher({ pathname, access }: SectionSwitcherProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  // Chiude su click esterno ed Escape mentre il pannello è aperto (come UserMenu).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const sections = accessibleSections(access);
  const active = sectionForPath(pathname);

  // Con una sola sezione accessibile non c'è nulla da selezionare: mostriamo
  // l'etichetta come testo statico (nessun dropdown).
  if (sections.length <= 1) {
    return (
      <span className="section-switcher section-switcher--static">
        <span className="section-switcher__trigger-icon" aria-hidden="true">
          <Icon name={active.icon} size={16} />
        </span>
        <span className="section-switcher__label">{active.label}</span>
      </span>
    );
  }

  const onSelect = (home: string) => {
    close();
    void navigate(home);
  };

  return (
    <div className="section-switcher" ref={containerRef}>
      <button
        type="button"
        className="ui-btn ui-btn--ghost section-switcher__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="section-switcher__trigger-icon" aria-hidden="true">
          <Icon name={active.icon} size={16} />
        </span>
        <span className="section-switcher__label">{active.label}</span>
        <span className="section-switcher__caret" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="section-switcher__panel"
          role="menu"
          aria-label="Sezioni dell'applicazione"
        >
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              role="menuitemradio"
              aria-checked={section.id === active.id}
              className={[
                'ui-btn section-switcher__item',
                section.id === active.id ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSelect(section.home)}
            >
              <span className="section-switcher__item-icon" aria-hidden="true">
                <Icon name={section.icon} size={16} />
              </span>
              <span className="section-switcher__item-label">{section.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
